import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown } from 'react-feather';
import { getArticle, getCategoryBySlug, getAllArticles } from './docsData';

const DocsArticle = ({ onNavigate }) => {
  const { category: categorySlug, article: articleSlug } = useParams();
  const [article, setArticle] = useState(null);
  const [category, setCategory] = useState(null);
  const [tableOfContents, setTableOfContents] = useState([]);
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [feedback, setFeedback] = useState(null); // 'useful' | 'not-useful' | null
  const [prevArticle, setPrevArticle] = useState(null);
  const [nextArticle, setNextArticle] = useState(null);

  const toggleFaq = (sectionIndex, itemIndex) => {
    const key = `${sectionIndex}-${itemIndex}`;
    setExpandedFaqs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleFeedback = (type) => {
    setFeedback(type);
    // Here you could send feedback to analytics/backend
  };

  useEffect(() => {
    const art = getArticle(categorySlug, articleSlug);
    const cat = getCategoryBySlug(categorySlug);
    
    setArticle(art);
    setCategory(cat);
    setFeedback(null); // Reset feedback when article changes

    // Get all articles and find prev/next
    const allArticles = getAllArticles();
    const currentIndex = allArticles.findIndex(
      a => a.categorySlug === categorySlug && a.slug === articleSlug
    );
    
    if (currentIndex > 0) {
      setPrevArticle(allArticles[currentIndex - 1]);
    } else {
      setPrevArticle(null);
    }
    
    if (currentIndex < allArticles.length - 1) {
      setNextArticle(allArticles[currentIndex + 1]);
    } else {
      setNextArticle(null);
    }

    // Extract table of contents from content
    if (art && art.content) {
      const headings = [];
      const lines = art.content.split('\n');
      lines.forEach((line) => {
        const trimmed = line.trim();
        if (trimmed.startsWith('## ')) {
          headings.push({
            level: 2,
            text: trimmed.substring(3),
            id: trimmed.substring(3).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
          });
        } else if (trimmed.startsWith('### ')) {
          headings.push({
            level: 3,
            text: trimmed.substring(4),
            id: trimmed.substring(4).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
          });
        }
      });
      setTableOfContents(headings);
    } else {
      setTableOfContents([]);
    }
  }, [categorySlug, articleSlug]);

  // Simple markdown renderer
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.trim().split('\n');
    const elements = [];
    let currentList = [];
    let listType = null;
    let skipFirstH1 = true;
    let currentTable = [];
    let inTable = false;

    const flushTable = () => {
      if (currentTable.length > 0) {
        const headerRow = currentTable[0];
        const bodyRows = currentTable.slice(2); // Skip header and separator
        
        elements.push(
          <div key={`table-${elements.length}`} style={{ 
            margin: '24px 0', 
            overflowX: 'auto',
            textAlign: 'left'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.95rem'
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(0, 0, 0, 0.1)' }}>
                  {headerRow.split('|').filter(cell => cell.trim()).map((cell, i) => (
                    <th key={i} style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#1a1a1a',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }}>
                      {cell.trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, rowIndex) => (
                  <tr key={rowIndex} style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                    {row.split('|').filter(cell => cell.trim()).map((cell, cellIndex) => (
                      <td key={cellIndex} style={{
                        padding: '12px 16px',
                        color: 'rgba(0, 0, 0, 0.8)',
                        textAlign: 'left'
                      }}>
                        {cell.trim()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        currentTable = [];
        inTable = false;
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        if (listType === 'ul') {
          elements.push(
            <ul key={`ul-${elements.length}`} style={{ 
              margin: '20px 0', 
              paddingLeft: '24px',
              lineHeight: '1.9'
            }}>
              {currentList.map((item, i) => (
                <li key={i} style={{ marginBottom: '10px', color: 'rgba(0, 0, 0, 0.8)' }}>
                  {renderInline(item)}
                </li>
              ))}
            </ul>
          );
        } else if (listType === 'ol') {
          elements.push(
            <ol key={`ol-${elements.length}`} style={{ 
              margin: '20px 0', 
              paddingLeft: '24px',
              lineHeight: '1.9'
            }}>
              {currentList.map((item, i) => (
                <li key={i} style={{ marginBottom: '10px', color: 'rgba(0, 0, 0, 0.8)' }}>
                  {renderInline(item)}
                </li>
              ))}
            </ol>
          );
        }
        currentList = [];
        listType = null;
      }
    };

    const renderInline = (text) => {
      // Bold
      text = text.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600; color: #1a1a1a;">$1</strong>');
      // Links
      text = text.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" style="color: #2563eb; text-decoration: none;">$1</a>');
      // Code
      text = text.replace(/`(.*?)`/g, '<code style="background: rgba(0,0,0,0.05); padding: 2px 6px; border-radius: 4px; font-size: 0.9em; font-family: monospace;">$1</code>');
      
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    };

    // Check if article has images
    const getImage = (imageName) => {
      if (article.images && article.images[imageName]) {
        return article.images[imageName];
      }
      return null;
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Check if this is a table row (contains | characters)
      const isTableRow = trimmed.includes('|') && (trimmed.startsWith('|') || trimmed.match(/^[^|]+\|/));
      const isTableSeparator = trimmed.match(/^\|?[\s\-:|]+\|[\s\-:|]+\|?$/);
      
      // Handle table rows
      if (isTableRow || isTableSeparator) {
        flushList();
        inTable = true;
        currentTable.push(trimmed);
        return;
      }
      
      // If we were in a table and hit a non-table line, flush the table
      if (inTable && !isTableRow && !isTableSeparator) {
        flushTable();
      }
      
      // Skip empty lines
      if (!trimmed) {
        flushList();
        flushTable();
        return;
      }

      // H1 - Skip the first one (we render title separately)
      if (trimmed.startsWith('# ')) {
        flushList();
        if (skipFirstH1) {
          skipFirstH1 = false;
          return;
        }
        return;
      }

      // H2
      if (trimmed.startsWith('## ')) {
        flushList();
        const headingText = trimmed.substring(3);
        const headingId = headingText.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        elements.push(
          <h2 key={index} id={headingId} style={{
            fontSize: '1.35rem',
            fontWeight: '600',
            color: '#1a1a1a',
            marginTop: '48px',
            marginBottom: '20px',
            lineHeight: '1.3'
          }}>
            {headingText}
          </h2>
        );
        return;
      }

      // H3
      if (trimmed.startsWith('### ')) {
        flushList();
        const headingText = trimmed.substring(4);
        const headingId = headingText.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        elements.push(
          <h3 key={index} id={headingId} style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#1a1a1a',
            marginTop: '36px',
            marginBottom: '16px',
            lineHeight: '1.3'
          }}>
            {headingText}
          </h3>
        );
        return;
      }

      // Unordered list
      if (trimmed.startsWith('- ')) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        currentList.push(trimmed.substring(2));
        return;
      }

      // Ordered list
      if (/^\d+\.\s/.test(trimmed)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        currentList.push(trimmed.replace(/^\d+\.\s/, ''));
        return;
      }

      // Image tag: ![alt](imageName)
      const imageMatch = trimmed.match(/^!\[(.*?)\]\((.*?)\)$/);
      if (imageMatch) {
        flushList();
        const altText = imageMatch[1];
        const imageName = imageMatch[2];
        const imageSrc = getImage(imageName);
        if (imageSrc) {
          elements.push(
            <div key={index} style={{
              margin: '32px 0',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
            }}>
              <img 
                src={imageSrc} 
                alt={altText}
                style={{
                  width: '100%',
                  display: 'block'
                }}
              />
            </div>
          );
        }
        return;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <p key={index} style={{
          fontSize: '1.05rem',
          color: 'rgba(0, 0, 0, 0.8)',
          lineHeight: '1.85',
          marginBottom: '20px'
        }}>
          {renderInline(trimmed)}
        </p>
      );
    });

    flushList();
    flushTable();
    return elements;
  };

  if (!article || !category) {
    return (
      <div style={{ padding: '60px 80px' }}>
        <p>Article not found</p>
      </div>
    );
  }

  return (
    <div className="docs-article-wrapper" style={{ 
      display: 'flex',
      gap: '80px',
      padding: '60px 80px',
      textAlign: 'left'
    }}>
      {/* Main Content */}
      <article className="docs-article-main" style={{ 
        flex: 1, 
        maxWidth: '720px',
        minWidth: 0,
        textAlign: 'left'
      }}>
        {/* Title */}
        <h1 className="docs-article-title" style={{
          fontSize: '2.25rem',
          fontWeight: '700',
          color: '#1a1a1a',
          marginBottom: '32px',
          letterSpacing: '-0.5px',
          lineHeight: '1.25',
          textAlign: 'left'
        }}>
          {article.title}
        </h1>

        {/* Video Embed (if article has video) */}
        {article.videoUrl && (
          <div style={{
            marginBottom: '32px',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9'
          }}>
            <iframe
              src={article.videoUrl}
              allow="autoplay"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title={article.title}
            />
          </div>
        )}

        {/* FAQ Content (if article is FAQ type) */}
        {article.isFaq && article.faqSections && (
          <div style={{ textAlign: 'left' }}>
            {article.faqSections.map((section, sectionIndex) => (
              <div key={sectionIndex} style={{ marginBottom: '40px' }}>
                <h2 style={{
                  fontSize: '1.35rem',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  marginBottom: '20px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                }}>
                  {section.title}
                </h2>
                <div>
                  {section.items.map((item, itemIndex) => {
                    const isExpanded = expandedFaqs[`${sectionIndex}-${itemIndex}`];
                    return (
                      <div 
                        key={itemIndex}
                        style={{
                          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                          marginBottom: '0'
                        }}
                      >
                        <button
                          onClick={() => toggleFaq(sectionIndex, itemIndex)}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '18px 0',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            gap: '16px'
                          }}
                        >
                          <span style={{
                            fontSize: '1.05rem',
                            fontWeight: '500',
                            color: '#1a1a1a',
                            flex: 1
                          }}>
                            {item.question}
                          </span>
                          <span style={{
                            fontSize: '1.5rem',
                            color: 'rgba(0, 0, 0, 0.4)',
                            fontWeight: '300',
                            lineHeight: 1,
                            transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}>
                            +
                          </span>
                        </button>
                        <div style={{
                          maxHeight: isExpanded ? '500px' : '0',
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease, padding 0.3s ease',
                          paddingBottom: isExpanded ? '18px' : '0'
                        }}>
                          <p style={{
                            fontSize: '1rem',
                            color: 'rgba(0, 0, 0, 0.7)',
                            lineHeight: '1.7',
                            margin: 0,
                            paddingRight: '40px'
                          }}>
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Regular Content (if not FAQ) */}
        {!article.isFaq && (
          <div style={{ textAlign: 'left' }}>
            {renderContent(article.content)}
          </div>
        )}

        {/* Previous / Next Navigation */}
        <div className="docs-article-prevnext" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          gap: '24px',
          marginTop: '64px',
          paddingTop: '32px',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          {prevArticle ? (
            <button
              onClick={() => onNavigate(prevArticle.categorySlug, prevArticle.slug)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '20px 24px',
                backgroundColor: '#ffffff',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <ChevronLeft size={20} style={{ color: 'rgba(0, 0, 0, 0.4)', flexShrink: 0 }} />
              <div>
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  color: 'rgba(0, 0, 0, 0.45)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '6px'
                }}>
                  Previous
                </div>
                <div style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#1a1a1a'
                }}>
                  {prevArticle.title}
                </div>
              </div>
            </button>
          ) : (
            <div style={{ flex: 1 }} />
          )}

          {nextArticle ? (
            <button
              onClick={() => onNavigate(nextArticle.categorySlug, nextArticle.slug)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '16px',
                padding: '20px 24px',
                backgroundColor: '#ffffff',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                cursor: 'pointer',
                textAlign: 'right',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div>
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  color: 'rgba(0, 0, 0, 0.45)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '6px'
                }}>
                  Next
                </div>
                <div style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#1a1a1a'
                }}>
                  {nextArticle.title}
                </div>
              </div>
              <ChevronRight size={20} style={{ color: 'rgba(0, 0, 0, 0.4)', flexShrink: 0 }} />
            </button>
          ) : (
            <div style={{ flex: 1 }} />
          )}
        </div>
      </article>

      {/* Right Sidebar - Table of Contents + Feedback */}
      <aside className="docs-article-aside" style={{
        width: '240px',
        flexShrink: 0,
        position: 'sticky',
        top: '124px',
        alignSelf: 'flex-start',
        maxHeight: 'calc(100vh - 180px)',
        overflowY: 'auto',
        textAlign: 'left'
      }}>
        {/* Table of Contents */}
        {tableOfContents.length > 0 && (
          <>
            <div style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: 'rgba(0, 0, 0, 0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              Table of Contents
            </div>
            <nav style={{ marginBottom: '40px' }}>
              {tableOfContents.map((heading, index) => (
                <a
                  key={index}
                  href={`#${heading.id}`}
                  style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    color: 'rgba(0, 0, 0, 0.55)',
                    textDecoration: 'none',
                    padding: '8px 0',
                    paddingLeft: heading.level === 3 ? '16px' : '0',
                    lineHeight: '1.45',
                    transition: 'color 0.15s',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#1a1a1a'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.55)'}
                >
                  {heading.text}
                </a>
              ))}
            </nav>
          </>
        )}

        {/* Feedback Section */}
        <div style={{
          paddingTop: tableOfContents.length > 0 ? '24px' : '0',
          borderTop: tableOfContents.length > 0 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'
        }}>
          <div style={{
            fontSize: '0.85rem',
            fontWeight: '500',
            color: '#1a1a1a',
            marginBottom: '12px',
            textAlign: 'left'
          }}>
            Was this helpful?
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handleFeedback('useful')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                backgroundColor: feedback === 'useful' ? '#1a1a1a' : '#ffffff',
                color: feedback === 'useful' ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                border: '1px solid',
                borderColor: feedback === 'useful' ? '#1a1a1a' : 'rgba(0, 0, 0, 0.15)',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (feedback !== 'useful') {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (feedback !== 'useful') {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.15)';
                }
              }}
            >
              <ThumbsUp size={14} />
              Yes
            </button>
            <button
              onClick={() => handleFeedback('not-useful')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                backgroundColor: feedback === 'not-useful' ? '#1a1a1a' : '#ffffff',
                color: feedback === 'not-useful' ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                border: '1px solid',
                borderColor: feedback === 'not-useful' ? '#1a1a1a' : 'rgba(0, 0, 0, 0.15)',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (feedback !== 'not-useful') {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (feedback !== 'not-useful') {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.15)';
                }
              }}
            >
              <ThumbsDown size={14} />
              No
            </button>
          </div>
          {feedback && (
            <div style={{
              marginTop: '12px',
              fontSize: '0.8rem',
              color: 'rgba(0, 0, 0, 0.5)'
            }}>
              Thanks for your feedback!
            </div>
          )}
        </div>
      </aside>

      {/* Mobile responsive overrides */}
      <style>{`
        @media (max-width: 768px) {
          .docs-article-wrapper {
            flex-direction: column !important;
            gap: 32px !important;
            padding: 24px 16px !important;
          }
          .docs-article-main {
            max-width: 100% !important;
          }
          .docs-article-title {
            font-size: 1.65rem !important;
            margin-bottom: 20px !important;
            line-height: 1.25 !important;
          }
          .docs-article-main h2 {
            font-size: 1.2rem !important;
            margin-top: 36px !important;
          }
          .docs-article-main h3 {
            font-size: 1.05rem !important;
            margin-top: 28px !important;
          }
          .docs-article-main p,
          .docs-article-main li {
            font-size: 1rem !important;
            line-height: 1.7 !important;
          }
          .docs-article-main ul,
          .docs-article-main ol {
            padding-left: 20px !important;
          }
          .docs-article-main table {
            font-size: 0.85rem !important;
          }
          .docs-article-main th,
          .docs-article-main td {
            padding: 8px 10px !important;
          }
          .docs-article-prevnext {
            flex-direction: column !important;
            gap: 12px !important;
            margin-top: 40px !important;
          }
          .docs-article-prevnext > * {
            width: 100% !important;
          }
          .docs-article-aside {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DocsArticle;